/**
 * Formateo de plata, en un solo lugar.
 *
 * Antes cada pantalla hacía `$${n.toFixed(2)}`, que daba "$10648.00": sin
 * separador de miles (a cinco cifras cuesta leerlo de un vistazo) y con dos
 * decimales que SIEMPRE son cero, porque las propinas se guardan como INT en
 * la base. Ese ".00" no aporta nada y en el número grande del dashboard se
 * comía buena parte del ancho.
 *
 * Ahora todo pasa por acá y sale "$ 10.648", con el formato de Uruguay.
 */

const formateador = (decimales) =>
  new Intl.NumberFormat('es-UY', {
    style: 'currency',
    currency: 'UYU',
    minimumFractionDigits: decimales ? 2 : 0,
    maximumFractionDigits: decimales ? 2 : 0,
  });

const sinDecimales = formateador(false);
const conDecimales = formateador(true);

/**
 * Monto en pesos. Por defecto sin decimales, que es lo correcto para las
 * propinas (son enteros). Pasar `{ decimales: true }` sólo para valores que
 * de verdad los tienen, como el valor del punto en el reparto por puntos.
 */
export const money = (n, { decimales = false } = {}) => {
  const valor = Number(n);
  if (!Number.isFinite(valor)) return decimales ? '$ 0,00' : '$ 0';
  return (decimales ? conDecimales : sinDecimales).format(valor);
};

/**
 * Monto abreviado, para los ticks de un eje donde "$ 368.001" no entra.
 * Redondea a miles a partir de 10.000: $ 368 mil.
 */
export const moneyCorto = (n) => {
  const valor = Number(n) || 0;
  if (Math.abs(valor) >= 10000) {
    return `$ ${new Intl.NumberFormat('es-UY', { maximumFractionDigits: 0 }).format(Math.round(valor / 1000))} mil`;
  }
  return money(valor);
};

/**
 * Convierte a número un valor escrito por una persona, aceptando la coma como
 * separador decimal, que es como se escribe en Uruguay. `Number('0,3')` da NaN.
 */
export const aNumero = (v) => {
  const n = Number(String(v ?? '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
};

/**
 * Deja pasar sólo lo que puede formar un número MIENTRAS se escribe, sin
 * romper los estados intermedios: "0", "0," y "0,3" son todos válidos.
 *
 * Hace falta porque estos campos son inputs controlados: si el valor mostrado
 * se recalculara desde el número en cada tecla, al escribir la coma de "0,3"
 * el parser devolvería 0 y la coma desaparecería. Nunca se podría tipear un
 * decimal. Normaliza el separador a coma y corta en dos decimales, que es lo
 * que la columna `puntos` guarda (DECIMAL(5,2)).
 */
export const limpiarNumero = (v) => {
  const soloValidos = String(v ?? '').replace(/[^0-9.,]/g, '');
  const partes = soloValidos.split(/[.,]/);
  if (partes.length <= 1) return partes[0] ?? '';
  // Si se empieza por el separador, se asume el cero: "," queda "0,".
  const entero = partes[0] || '0';
  return `${entero},${partes.slice(1).join('').slice(0, 2)}`;
};

/** Número suelto (cantidades, no plata) con separador de miles. */
export const numero = (n) => {
  const valor = Number(n);
  return Number.isFinite(valor) ? new Intl.NumberFormat('es-UY').format(valor) : '0';
};

/**
 * Porcentaje con un decimal como máximo, sin arrastrar ",0" cuando es entero.
 */
export const porcentaje = (n) => {
  const valor = Number(n) || 0;
  return `${new Intl.NumberFormat('es-UY', { maximumFractionDigits: 1 }).format(valor)}%`;
};
